import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import SendIcon from '@mui/icons-material/Send'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { useSnackbar } from 'notistack'
import { useAuth } from '@/hooks/useAuth'
import { documentoService } from '@/services/documentoService'
import { oportunidadeService } from '@/services/oportunidadeService'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { VersoesDocumento } from '@/components/documentos/VersoesDocumento'
import { ehConsultor } from '@/components/layout/menu'
import { Documento } from '@/types/documento'
import { mensagemErroDocumento } from '@/utils/mensagemErroDocumento'
import { AlertaItensBloqueantes, ChipStatusItem } from '@/components/documentos/ItemChecklistInfo'
import { itensBloqueantesDoErro } from '@/services/checklistReservaService'
import type { ItemBloqueanteChecklist, StatusItemChecklistAtual } from '@/types/checklist'
import {
  StatusDocObrigatorio,
  TIPO_ENTIDADE_OPORTUNIDADE,
  vincularDocumentos,
} from '@/utils/docsObrigatorios'

interface Props {
  open: boolean
  onClose: () => void
  reservaId: string
  produto: string
  empresa?: string
  /**
   * Status real da reserva. Em RASCUNHO o diálogo oferece "Enviar para aprovação"
   * assim que os obrigatórios estiverem completos (revisão de 06/09).
   */
  statusReserva?: string | null
  /** Callback opcional após cada upload bem-sucedido. */
  onUploadSucesso?: () => void
  /** Chamado após RASCUNHO → ENVIADA com sucesso (o pai recarrega a lista). */
  onEnviado?: () => void
}

/** Status em que o consultor ainda pode excluir/trocar documento (AN-4 §5). */
const STATUS_JANELA_EDICAO = ['RASCUNHO', 'COM_PENDENCIA']

const ACEITOS = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.png'

const formatSize = (bytes?: number) => {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

export const DocsObrigatoriosDialog: FC<Props> = ({
  open,
  onClose,
  reservaId,
  produto,
  empresa,
  statusReserva = null,
  onUploadSucesso,
  onEnviado,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const { usuario } = useAuth()
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [status, setStatus] = useState<StatusDocObrigatorio[]>([])
  const [uploadingLabel, setUploadingLabel] = useState<string | null>(null)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [excluindo, setExcluindo] = useState<{ doc: Documento; label: string } | null>(null)
  /** 422 `DOCUMENTOS_OBRIGATORIOS_FALTANDO`: a mensagem e os itens como o backend mandou. */
  const [bloqueio, setBloqueio] = useState<{ mensagem: string; itens: ItemBloqueanteChecklist[] } | null>(null)
  const consultor = ehConsultor(usuario?.permissoes)

  /**
   * O consultor só exclui/troca o documento da reserva que ELE enviou, com a reserva em
   * RASCUNHO ou COM_PENDENCIA (VC-16). Sem o status à mão, mostra e deixa o backend
   * responder (409 com o motivo). Back-office segue com a lixeira de sempre. Item já VALIDADO
   * pela Originei só o back-office invalida (P3) — o consultor não vê os botões.
   */
  const consultorPodeAlterar = (doc: Documento, statusItem?: StatusItemChecklistAtual | null) =>
    statusItem !== 'VALIDADO' &&
    (!doc.tipoEntidade || doc.tipoEntidade === TIPO_ENTIDADE_OPORTUNIDADE) &&
    (!doc.usuarioUpload || !usuario?.id || doc.usuarioUpload === usuario.id) &&
    (statusReserva == null || STATUS_JANELA_EDICAO.includes(statusReserva))

  const carregar = useCallback(async () => {
    if (!reservaId) return
    setLoading(true)
    setErro(null)
    try {
      // O checklist (o que é exigido e o que já consta) vem do backend — mesma regra
      // que bloqueia o envio. Os documentos vêm junto só para exibir/baixar o arquivo.
      const [checklist, page] = await Promise.all([
        oportunidadeService.documentosObrigatorios(reservaId),
        documentoService.listarFiltrado(TIPO_ENTIDADE_OPORTUNIDADE, reservaId, 0, 500),
      ])
      setStatus(vincularDocumentos(checklist, page.content ?? []))
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar documentos')
    } finally {
      setLoading(false)
    }
  }, [reservaId, produto])

  useEffect(() => {
    if (open) carregar()
  }, [open, carregar])

  // Só os OBRIGATÓRIOS contam como faltantes: opcional (V45) não segura o envio.
  const obrigatorios = useMemo(() => status.filter((s) => s.obrigatorio !== false), [status])
  const totalFaltantes = useMemo(
    () => obrigatorios.filter((s) => !s.presente).length,
    [obrigatorios]
  )
  const totalObrigatorios = obrigatorios.length
  const podeEnviar = statusReserva === 'RASCUNHO' && !loading && totalFaltantes === 0

  const handleUpload = async (label: string, file: File) => {
    setUploadingLabel(label)
    try {
      // O tipo e a chave vêm do próprio checklist do backend — não há mapa local a manter.
      const item = status.find((s) => s.label === label)
      await documentoService.upload(
        file, item?.tipo ?? 'OUTRO', reservaId, TIPO_ENTIDADE_OPORTUNIDADE, item?.chave
      )
      enqueueSnackbar(`${label} enviado`, { variant: 'success' })
      await carregar()
      onUploadSucesso?.()
    } catch (e) {
      enqueueSnackbar(
        e instanceof Error ? e.message : `Falha ao enviar ${label}`,
        { variant: 'error' }
      )
    } finally {
      setUploadingLabel(null)
    }
  }

  const handleRemover = async (docId: string, label: string) => {
    if (!window.confirm(`Remover o documento "${label}"?`)) return
    setRemovingId(docId)
    try {
      await documentoService.deletar(docId)
      enqueueSnackbar('Documento removido', { variant: 'success' })
      await carregar()
      onUploadSucesso?.()
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao remover', { variant: 'error' })
    } finally {
      setRemovingId(null)
    }
  }

  /** Exclusão pelo consultor (VC-16): confirmação no diálogo, mensagem do 409 legível. */
  const confirmarExclusaoConsultor = async () => {
    if (!excluindo) return
    const { doc } = excluindo
    setRemovingId(doc.id)
    try {
      await documentoService.remover(doc.id)
      enqueueSnackbar(`${doc.nome} excluído`, { variant: 'success' })
      setExcluindo(null)
      await carregar()
      onUploadSucesso?.()
    } catch (e) {
      enqueueSnackbar(mensagemErroDocumento(e, `Não foi possível excluir ${doc.nome}`), { variant: 'error' })
      setExcluindo(null)
    } finally {
      setRemovingId(null)
    }
  }

  const handleTrocar = async (doc: Documento, label: string, file: File) => {
    setRemovingId(doc.id)
    try {
      await documentoService.substituir(doc.id, file)
      enqueueSnackbar(`Arquivo de ${label} trocado`, { variant: 'success' })
      await carregar()
      onUploadSucesso?.()
    } catch (e) {
      enqueueSnackbar(mensagemErroDocumento(e, `Não foi possível trocar o arquivo de ${label}`), {
        variant: 'error',
      })
    } finally {
      setRemovingId(null)
    }
  }

  /** RASCUNHO → ENVIADA ("Aguardando aprovação"); o backend revalida o checklist. */
  const enviarParaAprovacao = async () => {
    if (!podeEnviar) return
    setEnviando(true)
    setBloqueio(null)
    try {
      await oportunidadeService.transicionarStatus(reservaId, {
        novoStatus: 'ENVIADA',
        usuarioId: usuario?.id ?? '',
      })
      enqueueSnackbar('Reserva enviada — aguardando aprovação da Originei.', { variant: 'success' })
      onEnviado?.()
      onClose()
    } catch (e) {
      const itens = itensBloqueantesDoErro(e)
      const mensagem =
        (e as { message?: string } | null)?.message || 'Não foi possível enviar a reserva para aprovação'
      if (itens.length > 0) setBloqueio({ mensagem, itens })
      else enqueueSnackbar(mensagem, { variant: 'error' })
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0, fontWeight: 700 }}>
        Documentos da reserva
        {empresa && (
          <Typography component="div" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {empresa} · {produto}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : status.length === 0 ? (
          <Alert severity="info">
            Não há documentos mapeados para o produto <strong>{produto}</strong>.
          </Alert>
        ) : (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                size="small"
                color={totalFaltantes === 0 ? 'success' : 'warning'}
                label={
                  totalFaltantes === 0
                    ? 'Todos os obrigatórios enviados'
                    : `${totalFaltantes} de ${totalObrigatorios} pendentes`
                }
              />
            </Stack>

            <List disablePadding>
              {status.map((item) => {
                const enviando = uploadingLabel === item.label
                const removendo = item.documento?.id === removingId
                return (
                  <ListItem
                    key={item.label}
                    sx={{
                      border: '1px solid',
                      borderColor: item.presente ? 'success.main' : 'divider',
                      borderRadius: 1.5,
                      mb: 1,
                      bgcolor: item.presente ? 'rgba(46,204,113,0.04)' : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.presente ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <InsertDriveFileIcon color="disabled" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{item.label}</span>
                          {item.status && <ChipStatusItem status={item.status} />}
                          {item.obrigatorio === false && (
                            <Chip size="small" variant="outlined" label="Opcional" />
                          )}
                        </Stack>
                      }
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', component: 'div' }}
                      secondary={
                        item.presente && item.documento ? (
                          <>
                            {`${item.documento.nome} (${formatSize(item.documento.tamanhoBytes)})`}
                            {/* Vigente + trilha de versões (L11/Q37). */}
                            <VersoesDocumento documento={item.documento} />
                          </>
                        ) : (
                          'Pendente — aguardando upload'
                        )
                      }
                      secondaryTypographyProps={{ fontSize: '0.75rem', component: 'div' }}
                    />
                    <ListItemSecondaryAction>
                      {item.presente && item.documento && consultor ? (
                        consultorPodeAlterar(item.documento, item.status) ? (
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Trocar arquivo">
                              <IconButton
                                component="label"
                                size="small"
                                disabled={removendo}
                                aria-label={`Trocar ${item.documento.nome}`}
                              >
                                {removendo ? <CircularProgress size={16} /> : <SwapHorizIcon fontSize="small" />}
                                <input
                                  type="file"
                                  hidden
                                  aria-label={`Arquivo novo para ${item.label}`}
                                  accept={ACEITOS}
                                  onChange={(e) => {
                                    const f = e.target.files?.[0]
                                    if (f) void handleTrocar(item.documento!, item.label, f)
                                    e.target.value = ''
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={removendo}
                                  aria-label={`Excluir ${item.documento.nome}`}
                                  onClick={() => setExcluindo({ doc: item.documento!, label: item.label })}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        ) : null
                      ) : item.presente && item.documento ? (
                        <IconButton
                          size="small"
                          onClick={() => handleRemover(item.documento!.id, item.label)}
                          disabled={removendo}
                        >
                          {removendo ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      ) : (
                        <Button
                          component="label"
                          size="small"
                          variant="outlined"
                          startIcon={
                            enviando ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <CloudUploadIcon />
                            )
                          }
                          disabled={enviando}
                        >
                          Enviar
                          <input
                            type="file"
                            hidden
                            aria-label={`Enviar ${item.label}`}
                            accept={ACEITOS}
                            onChange={(e) => {
                              const f = e.target.files?.[0]
                              if (f) handleUpload(item.label, f)
                              e.target.value = ''
                            }}
                          />
                        </Button>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                )
              })}
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              {statusReserva === 'RASCUNHO'
                ? 'Com todos os obrigatórios enviados, a reserva pode ser enviada para aprovação da Originei.'
                : 'Documentos opcionais podem ser complementados a qualquer momento.'}
            </Alert>
          </>
        )}
        {bloqueio && <AlertaItensBloqueantes mensagem={bloqueio.mensagem} itens={bloqueio.itens} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Fechar</Button>
        {statusReserva === 'RASCUNHO' && (
          <Button
            variant="contained"
            onClick={() => void enviarParaAprovacao()}
            disabled={!podeEnviar || enviando}
            startIcon={enviando ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
          >
            Enviar para aprovação
          </Button>
        )}
      </DialogActions>
      <ConfirmDialog
        open={excluindo != null}
        title="Excluir documento"
        message={excluindo ? `Excluir "${excluindo.doc.nome}" de ${excluindo.label}? O item volta a ficar pendente.` : ''}
        confirmLabel="Excluir"
        isDangerous
        isLoading={excluindo != null && removingId === excluindo.doc.id}
        onConfirm={() => void confirmarExclusaoConsultor()}
        onCancel={() => setExcluindo(null)}
      />
    </Dialog>
  )
}
